"use client";

import { useServerErrorMessage } from "@/lib/i18n/use-server-error-message";
import { useUIMessages } from "@convex-dev/agent/react";
import { api } from "@v1/backend/convex/_generated/api";
import type { Id } from "@v1/backend/convex/_generated/dataModel";
import { useMutation, useQuery } from "convex/react";
import { useTranslations } from "next-intl";
import { type ReactElement, useMemo } from "react";
import { toast } from "sonner";
import { MessengerAgentAvatar, MessengerTeamAvatar } from "./messenger-avatars";
import { type MessengerBubbleItem, MessengerBubbles } from "./messenger-bubbles";
import { MessengerChatMenu } from "./messenger-chat-menu";
import { MessengerComposer } from "./messenger-composer";
import { useMessenger, useMessengerSurface } from "./messenger-context";
import { MessengerDetailHeader } from "./messenger-header";
import { MessengerTicketThread } from "./messenger-ticket-thread";
import {
  MessengerEventRow,
  MessengerFailedRow,
  MessengerLoadOlder,
  MessengerStatusRow,
  MessengerTicketSubtitle,
  MessengerTypingBubble,
} from "./messenger-thread-parts";
import { useMessengerMarkRead } from "./use-messenger-mark-read";
import { isAwaitingAgentAnswer, isFullUIMessage, toMessengerBubble } from "./messenger-ui-message";
import { useMessengerPendingSend } from "./use-messenger-pending-send";
import { useMessengerStickToBottom } from "./use-messenger-scroll";

const PAGE_SIZE = 30;

/** The first message as sent, until the server returns it. */
function pendingBubble(text: string): MessengerBubbleItem {
  return { id: "pending", author: "self", name: null, text, at: null };
}

/** New chat: welcome text, the first message creates the chat. */
function NewChat(): ReactElement {
  const t = useTranslations("messenger.thread");
  const errorMessage = useServerErrorMessage();
  const tLists = useTranslations("messenger.lists");
  const { config, nav } = useMessenger();
  const surface = useMessengerSurface();
  const start = useMutation(api.customerChat.messenger_writes.startChat);
  const welcome: MessengerBubbleItem[] =
    config.welcome_message.length > 0
      ? [{ id: "welcome", author: "agent", name: null, text: config.welcome_message, at: null }]
      : [];
  return (
    <>
      <MessengerDetailHeader title={tLists("neueNachricht")} leading={<MessengerAgentAvatar size="md" />} />
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
        <MessengerBubbles items={welcome} />
      </div>
      <MessengerComposer
        autoFocus
        onSend={async (text) => {
          try {
            const result = await start({
              surface,
              text,
              ...(surface === "app" ? { page_path: window.location.pathname } : {}),
            });
            nav.replace({ kind: "chat", sessionId: result.session_id, pendingText: text });
            return true;
          } catch (error) {
            toast.error(errorMessage(error, t("fehler")));
            return false;
          }
        }}
      />
    </>
  );
}

function AgentThread({
  sessionId,
  threadId,
  failed,
  ticketPending,
  pendingText,
}: {
  readonly sessionId: Id<"public_chat_sessions">;
  readonly threadId: string;
  readonly failed: boolean;
  /** Chat is becoming a ticket: no composer, status line instead. */
  readonly ticketPending: { readonly at: number } | null;
  readonly pendingText: string | null;
}): ReactElement {
  const t = useTranslations("messenger.thread");
  const errorMessage = useServerErrorMessage();
  const surface = useMessengerSurface();
  const send = useMutation(api.customerChat.messenger_writes.sendChatMessage);
  const handToTeam = useMutation(api.customerChat.messenger_writes.handChatToTeam);
  const { results, status, loadMore } = useUIMessages(
    api.customerChat.messenger_thread.listChatMessages,
    { surface, session_id: sessionId, threadId },
    { initialNumItems: PAGE_SIZE, stream: true },
  );
  const rows = useMemo(() => results.filter(isFullUIMessage), [results]);
  const pending = useMessengerPendingSend(rows, status !== "LoadingFirstPage");
  const showPending = pendingText !== null && !results.some((m) => m.role === "user");
  const items = useMemo((): MessengerBubbleItem[] => {
    const loaded = rows.map(toMessengerBubble).filter((m): m is MessengerBubbleItem => m !== null);
    if (showPending) loaded.push(pendingBubble(pendingText ?? ""));
    if (pending.bubble !== null) loaded.push(pending.bubble);
    return loaded;
  }, [pending.bubble, pendingText, rows, showPending]);
  const awaitingAnswer =
    ticketPending === null &&
    !failed &&
    (showPending || pending.bubble !== null || isAwaitingAgentAnswer(rows[rows.length - 1]));
  const lastItem = items[items.length - 1];
  const scrollRef = useMessengerStickToBottom(
    `${items.length}:${lastItem?.text.length ?? 0}:${lastItem?.tools?.length ?? 0}:${awaitingAnswer}`,
  );

  const onHandToTeam = (): void => {
    void handToTeam({ surface, session_id: sessionId }).catch((error: unknown) =>
      toast.error(errorMessage(error, t("fehler"))),
    );
  };

  return (
    <>
      <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
        {status === "CanLoadMore" ? <MessengerLoadOlder onClick={() => loadMore(PAGE_SIZE)} /> : null}
        <MessengerBubbles
          items={items}
          footer={
            <>
              {awaitingAnswer ? <MessengerTypingBubble /> : null}
              {failed && ticketPending === null ? <MessengerFailedRow onHandToTeam={onHandToTeam} /> : null}
            </>
          }
        />
        {ticketPending !== null ? (
          <>
            <MessengerEventRow at={ticketPending.at} />
            <MessengerStatusRow text={t("wirdAngelegt")} />
          </>
        ) : null}
      </div>
      {ticketPending === null ? (
        <MessengerComposer
          clearAtOnce
          onSend={(text) =>
            pending.track(text, async () => {
              try {
                await send({ surface, session_id: sessionId, text });
                return true;
              } catch (error) {
                toast.error(errorMessage(error, t("fehler")));
                return false;
              }
            })
          }
        />
      ) : null}
    </>
  );
}

function ExistingChat({
  sessionId,
  pendingText,
}: {
  readonly sessionId: Id<"public_chat_sessions">;
  readonly pendingText: string | null;
}): ReactElement | null {
  const tLists = useTranslations("messenger.lists");
  const surface = useMessengerSurface();
  const chat = useQuery(api.customerChat.messenger_thread.getChat, { surface, session_id: sessionId });
  useMessengerMarkRead({ kind: "chat", sessionId });
  useMessengerMarkRead(chat?.ticket ? { kind: "ticket", taskId: chat.ticket.summary.task_id } : null);
  if (chat === undefined || chat === null) {
    return (
      <>
        <MessengerDetailHeader title={pendingText ?? ""} leading={<MessengerAgentAvatar size="md" />} />
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
          {chat === undefined && pendingText !== null ? (
            <MessengerBubbles items={[pendingBubble(pendingText)]} footer={<MessengerTypingBubble />} />
          ) : null}
        </div>
      </>
    );
  }
  const ticket = chat.ticket;
  return (
    <>
      <MessengerDetailHeader
        title={ticket?.summary.title || chat.title || tLists("neueNachricht")}
        leading={ticket !== null ? <MessengerTeamAvatar /> : <MessengerAgentAvatar size="md" />}
        subtitle={
          ticket ? <MessengerTicketSubtitle number={ticket.summary.number} status={ticket.summary.status} /> : null
        }
        actions={ticket === null ? <MessengerChatMenu sessionId={sessionId} /> : null}
      />
      {ticket !== null && ticket.state === "ready" ? (
        <MessengerTicketThread
          taskId={ticket.summary.task_id}
          done={ticket.summary.status === "done"}
          handedAt={ticket.converted_at}
        />
      ) : (
        <AgentThread
          sessionId={sessionId}
          threadId={chat.thread_id}
          failed={chat.turn_failed}
          ticketPending={ticket === null ? null : { at: ticket.converted_at }}
          pendingText={pendingText}
        />
      )}
    </>
  );
}

export function MessengerChatView({
  sessionId,
  pendingText,
}: {
  readonly sessionId: Id<"public_chat_sessions"> | null;
  readonly pendingText?: string;
}): ReactElement | null {
  return sessionId === null ? (
    <NewChat />
  ) : (
    <ExistingChat key={sessionId} sessionId={sessionId} pendingText={pendingText ?? null} />
  );
}
